/**
 * 宇宙史ページで使う出来事データ。
 *
 * - EARLY_UNIVERSE_EVENTS: ビッグバン直後の対数スケールタイムライン用
 *   (プランク時代〜再結合期。秒単位)
 * - CALENDAR_EVENTS: 宇宙暦(138億年を1年に圧縮)用。「〜年前」で指定する
 *
 * 年代の出典は標準的な宇宙論・地質学・人類学の教科書レベルの値
 * (Planck 2018 宇宙論パラメータ、地質年代表、人類学の一般的な推定値)。
 */

export interface EarlyUniverseEvent {
  id: string;
  secondsAfterBigBang: number;
  nameJa: string;
  nameEn: string;
  descJa: string;
  descEn: string;
}

export const EARLY_UNIVERSE_EVENTS: EarlyUniverseEvent[] = [
  {
    id: 'planck',
    secondsAfterBigBang: 5.4e-44,
    nameJa: 'プランク時代の終わり',
    nameEn: 'End of Planck Epoch',
    descJa: 'これより前は既知の物理法則が適用できない領域。量子重力理論が必要。',
    descEn: 'Before this, known physics breaks down — a regime requiring quantum gravity.',
  },
  {
    id: 'inflation',
    secondsAfterBigBang: 1e-32,
    nameJa: 'インフレーション終了',
    nameEn: 'End of Cosmic Inflation',
    descJa: '宇宙が指数関数的に急膨張した期間が終わる。現在の宇宙の平坦性・一様性の起源。',
    descEn: 'The end of exponential expansion that explains the universe\'s flatness and uniformity.',
  },
  {
    id: 'quark',
    secondsAfterBigBang: 1e-6,
    nameJa: 'クォーク時代の終わり',
    nameEn: 'End of Quark Epoch',
    descJa: 'クォークが結合し、陽子・中性子(ハドロン)が形成され始める。',
    descEn: 'Quarks combine to form protons and neutrons (hadrons).',
  },
  {
    id: 'bbn',
    secondsAfterBigBang: 180,
    nameJa: 'ビッグバン元素合成',
    nameEn: 'Big Bang Nucleosynthesis',
    descJa: '水素・ヘリウムなど軽元素の大部分がこの数分間で作られる。',
    descEn: 'Most of the universe\'s hydrogen and helium form within these few minutes.',
  },
  {
    id: 'matter-radiation-eq',
    secondsAfterBigBang: 47000 * 365.25 * 24 * 3600,
    nameJa: '物質・放射の等密度時代',
    nameEn: 'Matter-Radiation Equality',
    descJa: '物質の密度が放射(光子)の密度を上回り、構造形成が始まる。',
    descEn: 'Matter density overtakes radiation density, enabling structure formation.',
  },
  {
    id: 'recombination',
    secondsAfterBigBang: 380000 * 365.25 * 24 * 3600,
    nameJa: '再結合期(宇宙背景放射の起源)',
    nameEn: 'Recombination (Origin of the CMB)',
    descJa: '宇宙が晴れ上がり、光がまっすぐ進めるようになる。今日観測される宇宙マイクロ波背景放射(CMB)はこの時の光。',
    descEn: 'The universe becomes transparent to light — the CMB we observe today is light from this moment.',
  },
  {
    id: 'first-stars',
    secondsAfterBigBang: 2e8 * 365.25 * 24 * 3600,
    nameJa: '最初の星の誕生',
    nameEn: 'First Stars Form',
    descJa: '「宇宙の暗黒時代」が終わり、最初の星々が輝き始める。',
    descEn: 'The "cosmic dark ages" end as the first stars ignite.',
  },
  {
    id: 'reionization',
    secondsAfterBigBang: 1e9 * 365.25 * 24 * 3600,
    nameJa: '再電離完了',
    nameEn: 'Reionization Complete',
    descJa: '最初の星々からの紫外線が宇宙全体の水素を再び電離させる。',
    descEn: 'UV light from the first stars re-ionizes hydrogen throughout the universe.',
  },
];

export interface CalendarEvent {
  id: string;
  yearsAgo: number;
  nameJa: string;
  nameEn: string;
  descJa: string;
  descEn: string;
  category: 'cosmos' | 'earth' | 'life' | 'human';
}

export const CALENDAR_EVENTS: CalendarEvent[] = [
  { id: 'bigbang', yearsAgo: 13.8e9, nameJa: 'ビッグバン', nameEn: 'Big Bang', descJa: '宇宙の始まり。宇宙暦の1月1日 0:00。', descEn: 'The beginning of the universe. Jan 1, 00:00 on the cosmic calendar.', category: 'cosmos' },
  { id: 'milkyway', yearsAgo: 13.6e9, nameJa: '天の川銀河の形成開始', nameEn: 'Milky Way Begins Forming', descJa: '我々の銀河系の最初の星々が形成され始める。', descEn: 'The first stars of our home galaxy begin to form.', category: 'cosmos' },
  { id: 'solarsystem', yearsAgo: 4.6e9, nameJa: '太陽系の形成', nameEn: 'Solar System Forms', descJa: '太陽と原始惑星系円盤が形成される。', descEn: 'The Sun and protoplanetary disk form.', category: 'cosmos' },
  { id: 'earth', yearsAgo: 4.54e9, nameJa: '地球の形成', nameEn: 'Earth Forms', descJa: '微惑星の衝突・集積により地球が形成される。', descEn: 'Earth forms through the accretion of planetesimals.', category: 'earth' },
  { id: 'firstlife', yearsAgo: 3.8e9, nameJa: '最古の生命(推定)', nameEn: 'Earliest Life (estimated)', descJa: '単純な単細胞生物が地球上に現れる。', descEn: 'Simple single-celled organisms appear on Earth.', category: 'life' },
  { id: 'photosynthesis', yearsAgo: 2.4e9, nameJa: '大酸化イベント', nameEn: 'Great Oxidation Event', descJa: '光合成生物により大気中に酸素が急増し始める。', descEn: 'Photosynthetic organisms begin filling the atmosphere with oxygen.', category: 'life' },
  { id: 'multicellular', yearsAgo: 1.5e9, nameJa: '多細胞生物の出現', nameEn: 'Multicellular Life Appears', descJa: '複数の細胞からなる生物が現れ始める。', descEn: 'Organisms made of multiple cells begin to appear.', category: 'life' },
  { id: 'cambrian', yearsAgo: 5.4e8, nameJa: 'カンブリア爆発', nameEn: 'Cambrian Explosion', descJa: '多様な動物の体制(門)が爆発的に出現する。', descEn: 'A dramatic diversification of animal body plans occurs.', category: 'life' },
  { id: 'dinosaurs-rise', yearsAgo: 2.3e8, nameJa: '恐竜の繁栄開始', nameEn: 'Age of Dinosaurs Begins', descJa: '恐竜が陸上生態系の頂点に立つ時代が始まる。', descEn: 'Dinosaurs begin their reign as dominant land animals.', category: 'life' },
  { id: 'kpg', yearsAgo: 6.6e7, nameJa: '恐竜の絶滅(K-Pg境界)', nameEn: 'Dinosaur Extinction (K-Pg)', descJa: '小惑星衝突により恐竜を含む大量絶滅が起こる。哺乳類の時代への転換点。', descEn: 'An asteroid impact causes mass extinction, opening the way for mammals.', category: 'life' },
  { id: 'primates', yearsAgo: 6e7, nameJa: '霊長類の出現', nameEn: 'Primates Appear', descJa: '最初期の霊長類が現れる。', descEn: 'The earliest primates appear.', category: 'life' },
  { id: 'humans', yearsAgo: 3e5, nameJa: 'ホモ・サピエンスの誕生', nameEn: 'Homo Sapiens Emerge', descJa: '現生人類が誕生する。宇宙暦では大晦日、深夜0時のわずか数分前。', descEn: 'Modern humans emerge — just minutes before midnight on the cosmic calendar.', category: 'human' },
  { id: 'agriculture', yearsAgo: 12000, nameJa: '農耕の始まり', nameEn: 'Agriculture Begins', descJa: '人類が定住し、農耕を始める。', descEn: 'Humans settle and begin farming.', category: 'human' },
  { id: 'writing', yearsAgo: 5000, nameJa: '文字の発明・文明の記録開始', nameEn: 'Writing Invented / Recorded History Begins', descJa: 'メソポタミア等で文字が発明され、記録に残る歴史が始まる。宇宙暦では大晦日、最後の10数秒。', descEn: 'Writing is invented in Mesopotamia and elsewhere — recorded history begins in the final seconds of the cosmic year.', category: 'human' },
  { id: 'now', yearsAgo: 0, nameJa: '現在', nameEn: 'Now', descJa: '宇宙暦の12月31日 24:00。', descEn: 'Dec 31, 24:00 on the cosmic calendar.', category: 'human' },
];
